import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { IntlShape, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../common/types';
import { platformMeta } from '../promote/platformMeta';
import { SerializedSocialPost } from '../promote/types';
import { Music24Mark } from './Music24Mark';
import { SocialPostThumb } from './SocialPostThumb';
import { SocialPostTypeBadge } from './SocialPostTypeBadge';
import { MUSIC24_MARK, socialMessages as messages } from './socialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../promote/Promote.module.scss';
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostRowProps {
  post: SerializedSocialPost;
  isOpen: boolean;
  /** The table has the "Link clicks" column (X). A row of another platform shows a dash there. */
  showLinkClicks?: boolean;
  onOpen: (post: SerializedSocialPost) => void;
}

const tableNumbers = ['views', 'likes', 'comments', 'shares', 'saves'] as const;
const tableNumbersWithLinkClicks = [...tableNumbers, 'linkClicks'] as const;

/** A number that the platform does not give is a dash, never 0. */
function formatCount(intl: IntlShape, value: number | null): string {
  return value === null ? '—' : intl.formatNumber(value);
}

/**
 * One post of the table. Below 769 px the same row is a list item: the type, the date and three
 * numbers move under the caption, and the number columns hide (design c3).
 */
const SocialPostRowComponent: React.FC<SocialPostRowProps> = ({ post, isOpen, showLinkClicks = false, onOpen }) => {
  const intl = useIntl();
  const meta = platformMeta[post.platform];
  const open = useCallback(() => onOpen(post), [onOpen, post]);
  const cells = useMemo(
    () =>
      (showLinkClicks ? tableNumbersWithLinkClicks : tableNumbers).map((key) => (
        <span
          key={key}
          className={clsx(styles.postNumber, styles.desktopOnly, post[key] === null && shared.dash)}
          data-testid={`social-post-${key}`}
        >
          {formatCount(intl, post[key])}
        </span>
      )),
    [intl, post, showLinkClicks]
  );
  const date = intl.formatDate(new Date(post.publishedAt), { month: 'short', day: 'numeric' });
  const isOurs = post.scheduledPostUuid !== null;
  const hasCaption = post.caption !== null && post.caption.trim() !== '';

  return (
    <button
      type="button"
      className={clsx(styles.postRow, showLinkClicks && styles.postGridLinkClicks, isOpen && styles.postRowOpen)}
      onClick={open}
    >
      <span className={styles.postCell}>
        <SocialPostThumb post={post} />
        <span className={styles.postText}>
          <b className={clsx(styles.postCaption, !hasCaption && shared.dash)}>
            {hasCaption ? post.caption : intl.formatMessage(messages.noCaption)}
          </b>
          <span className={styles.postMeta}>
            <span className={styles.postChannel} style={{ color: meta.color }}>
              <FontAwesomeIcon icon={meta.icon} />
            </span>
            <span className={styles.desktopOnly}>{meta.name}</span>
            <span className={styles.phoneOnly}>
              <SocialPostTypeBadge type={post.type} />
            </span>
            <span className={styles.phoneOnly}>{date}</span>
            {isOurs ? <Music24Mark label={MUSIC24_MARK} /> : null}
          </span>
          <span className={clsx(styles.phoneNumbers, styles.phoneOnly)}>
            <span>{intl.formatMessage(messages.phoneViews, { count: formatCount(intl, post.views) })}</span>
            <span>{intl.formatMessage(messages.phoneLikes, { count: formatCount(intl, post.likes) })}</span>
            <span>{intl.formatMessage(messages.phoneComments, { count: formatCount(intl, post.comments) })}</span>
          </span>
        </span>
      </span>
      <span className={styles.desktopOnly}>
        <SocialPostTypeBadge type={post.type} />
      </span>
      <span className={clsx(styles.postDate, styles.desktopOnly)}>{date}</span>
      {cells}
    </button>
  );
};

export const SocialPostRow = TypedMemo(SocialPostRowComponent);
